/**
 * chatTabsStore — vibe-designer 1.83.0 Pi chat tabs.
 *
 * Per-tenant zustand store for the Pi chat tab strip: id-keyed tab list
 * (where each id doubles as the Pi sessionId) + an activeTabId. Metadata
 * persists to localStorage `vibe_pi_chat_tabs_${tenantId}` so the open-tab
 * set survives a refresh; chat content lives in Pi's IndexedDB
 * `vibe-pi-chat-${tenantId}` via SessionsStore (handled by ChatPanelPi).
 *
 * On `hydrate(tenantId)` the store reads localStorage AND reconciles
 * against `getAppStorage().sessions.getAllMetadata()` — Pi sessions present
 * in IndexedDB but missing from localStorage are appended at the end as
 * "orphan" tabs (e.g. localStorage was cleared but IndexedDB persisted).
 *
 * Mirrors chatPrefsStore + chatHarnessStore patterns: STORAGE_KEY_PREFIX +
 * try/catch storage + default fallback. The IndexedDB call is best-effort
 * — if `getAppStorage()` hasn't been initialized yet the store falls back
 * to localStorage-only state without throwing.
 */
import { create } from 'zustand';
import { getAppStorage } from '@earendil-works/pi-web-ui';
import { randomUUID } from '../utils/randomUUID';
import type { GhlThemeId } from '@/shared/theme/agentThemes';

/** What backs a chat tab: a Pi Agent session, or the embedded Hermes chat. */
export type ChatTabKind = 'pi' | 'hermes';

export interface ChatTab {
  /** uuid; for a Pi tab it ALSO serves as the Pi sessionId. */
  id: string;
  /** User-facing title. Initially "New chat N"; updated to Pi-derived after first exchange. */
  label: string;
  /** epoch ms — used for stable ordering of orphan tabs after reconciliation. */
  createdAt: number;
  /** 2026-07-04 — per-tab GHL theme (undefined → tenant/global default). */
  themeId?: GhlThemeId;
  /**
   * 2026-07-04 — tab backend. `'pi'` (default; undefined also = pi for
   * back-compat with tabs persisted before this field) is a Pi Agent session
   * whose id is the Pi sessionId. `'hermes'` is the embedded Hermes chat
   * iframe — it has NO Pi session, so Pi-session logic (IndexedDB delete,
   * setAgent bind) must be skipped for it.
   */
  kind?: ChatTabKind;
}

/** True if any tab is a Hermes tab (used to enforce the one-Hermes-tab cap). */
export function hasHermesTab(tabs: ChatTab[]): boolean {
  return tabs.some((t) => t.kind === 'hermes');
}

interface PersistedShape {
  tabs: ChatTab[];
  activeTabId: string | null;
}

const MAX_TABS = 4;
const STORAGE_KEY_PREFIX = 'vibe_pi_chat_tabs_';

export const CHAT_TABS_MAX = MAX_TABS;

function storageKeyFor(tenantId: string): string {
  return `${STORAGE_KEY_PREFIX}${tenantId}`;
}

function readStored(tenantId: string): PersistedShape {
  try {
    const raw = localStorage.getItem(storageKeyFor(tenantId));
    if (!raw) return { tabs: [], activeTabId: null };
    const parsed = JSON.parse(raw) as PersistedShape;
    if (!parsed || !Array.isArray(parsed.tabs)) {
      return { tabs: [], activeTabId: null };
    }
    return {
      tabs: parsed.tabs.filter(
        (t): t is ChatTab =>
          typeof t?.id === 'string' &&
          typeof t?.label === 'string' &&
          typeof t?.createdAt === 'number',
      ),
      activeTabId: typeof parsed.activeTabId === 'string' ? parsed.activeTabId : null,
    };
  } catch {
    return { tabs: [], activeTabId: null };
  }
}

function writeStored(tenantId: string, shape: PersistedShape): void {
  try {
    localStorage.setItem(storageKeyFor(tenantId), JSON.stringify(shape));
  } catch {
    // Quota / disabled storage — runtime stays correct.
  }
}

interface ChatTabsState {
  tabs: ChatTab[];
  activeTabId: string | null;
  /** Restore from localStorage + reconcile with Pi IndexedDB; null tenantId clears. */
  hydrate: (tenantId: string | null) => Promise<void>;
  /**
   * Append a new tab + activate. Returns null at MAX_TABS, or when adding a
   * second Hermes tab (only one is allowed — a single hermes instance/session
   * per tenant). Caller may toast.
   */
  newTab: (tenantId: string, kind?: ChatTabKind) => ChatTab | null;
  /** Remove tab from store + delete the Pi session. Best-effort delete. */
  closeTab: (tenantId: string, tabId: string) => Promise<void>;
  /** Set active tab. No-op if id is unknown. */
  setActiveTab: (tenantId: string, tabId: string) => void;
  /** Update a tab's label and persist. */
  updateLabel: (tenantId: string, tabId: string, label: string) => void;
  /** Set a tab's per-tab GHL theme and persist. No-op if id is unknown. */
  setTabTheme: (tenantId: string, tabId: string, themeId: GhlThemeId) => void;
}

export const useChatTabsStore = create<ChatTabsState>((set, get) => ({
  tabs: [],
  activeTabId: null,

  hydrate: async (tenantId) => {
    if (!tenantId) {
      set({ tabs: [], activeTabId: null });
      return;
    }
    const stored = readStored(tenantId);
    const tabs: ChatTab[] = [...stored.tabs];
    let activeTabId: string | null = stored.activeTabId;
    try {
      const allMeta = await getAppStorage().sessions.getAllMetadata();
      const knownIds = new Set(tabs.map((t) => t.id));
      for (const meta of allMeta) {
        if (!knownIds.has(meta.id)) {
          const createdMs = Date.parse(meta.createdAt);
          tabs.push({
            id: meta.id,
            label: meta.title || 'Restored chat',
            createdAt: Number.isFinite(createdMs) ? createdMs : Date.now(),
          });
        }
      }
    } catch {
      // AppStorage not initialized yet (very early hydrate) — fall back to
      // localStorage-only state. The next hydrate cycle reconciles.
    }
    if (activeTabId && !tabs.find((t) => t.id === activeTabId)) {
      activeTabId = tabs[0]?.id ?? null;
    }
    set({ tabs, activeTabId });
    writeStored(tenantId, { tabs, activeTabId });
  },

  newTab: (tenantId, kind = 'pi') => {
    const { tabs } = get();
    if (tabs.length >= MAX_TABS) return null;
    // One hermes instance/session per tenant → at most one Hermes tab.
    if (kind === 'hermes' && hasHermesTab(tabs)) return null;
    const tab: ChatTab = {
      id: randomUUID(),
      label: kind === 'hermes' ? 'Hermes' : `New chat ${tabs.length + 1}`,
      createdAt: Date.now(),
      kind,
    };
    const nextTabs = [...tabs, tab];
    set({ tabs: nextTabs, activeTabId: tab.id });
    writeStored(tenantId, { tabs: nextTabs, activeTabId: tab.id });
    return tab;
  },

  closeTab: async (tenantId, tabId) => {
    // Only Pi tabs have a Pi IndexedDB session — a Hermes tab (kind:'hermes')
    // has none, so skip the delete entirely for it (undefined kind = pi).
    const closing = get().tabs.find((t) => t.id === tabId);
    if ((closing?.kind ?? 'pi') === 'pi') {
      try {
        await getAppStorage().sessions.delete(tabId);
      } catch (err) {
        // Pi storage not initialized OR session was never saved (no first
        // exchange yet) — both are normal. Genuine IndexedDB failures
        // (quota, corruption) also land here; we log so they show in the
        // browser console but proceed with local tab removal regardless
        // (the user wanted the tab gone). G1 reviewer Concern 1 fix.
        console.warn('[chatTabsStore] sessions.delete failed; proceeding with local removal:', err);
      }
    }
    const state = get();
    const idx = state.tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return;
    const nextTabs = state.tabs.filter((t) => t.id !== tabId);
    let nextActive = state.activeTabId;
    if (state.activeTabId === tabId) {
      // Pick next tab forward if it exists; otherwise previous; otherwise null.
      nextActive = nextTabs[idx]?.id ?? nextTabs[idx - 1]?.id ?? null;
    }
    set({ tabs: nextTabs, activeTabId: nextActive });
    writeStored(tenantId, { tabs: nextTabs, activeTabId: nextActive });
  },

  setActiveTab: (tenantId, tabId) => {
    const { tabs } = get();
    if (!tabs.find((t) => t.id === tabId)) return;
    set({ activeTabId: tabId });
    writeStored(tenantId, { tabs, activeTabId: tabId });
  },

  updateLabel: (tenantId, tabId, label) => {
    const state = get();
    const idx = state.tabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return;
    const nextTabs = state.tabs.map((t) =>
      t.id === tabId ? { ...t, label } : t,
    );
    set({ tabs: nextTabs });
    writeStored(tenantId, { tabs: nextTabs, activeTabId: state.activeTabId });
  },

  setTabTheme: (tenantId, tabId, themeId) => {
    const state = get();
    if (!state.tabs.find((t) => t.id === tabId)) return;
    const nextTabs = state.tabs.map((t) =>
      t.id === tabId ? { ...t, themeId } : t,
    );
    set({ tabs: nextTabs });
    writeStored(tenantId, { tabs: nextTabs, activeTabId: state.activeTabId });
  },
}));
